import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageHeader from "../../components/PageHeader";
import ButtonFilled from "../../components/FormElements/ButtonFilled";
import {
    cancelCampaign,
    fetchCampaign,
    fetchCampaignAnalytics,
    pauseCampaign,
    resumeCampaign,
} from "../../store/campaignStore/campaignActions";
import RecipientList from "./RecipientList";
import styles from "./campaigns.module.scss";

const STATUS_LABEL_KEY = {
    draft: "NSCampaigns.statusDraft",
    scheduled: "NSCampaigns.statusScheduled",
    in_progress: "NSCampaigns.statusInProgress",
    paused: "NSCampaigns.statusPaused",
    completed: "NSCampaigns.statusCompleted",
    failed: "NSCampaigns.statusFailed",
    cancelled: "NSCampaigns.statusCancelled",
};

const WINDOW_OPTIONS = [7, 14, 30];

const DELIVERY_CARDS = [
    { key: "total", labelKey: "NSCampaigns.colTotal" },
    { key: "sent", labelKey: "NSCampaigns.colSent" },
    { key: "delivered", labelKey: "NSCampaigns.colDelivered" },
    { key: "read", labelKey: "NSCampaigns.colRead" },
    { key: "failed", labelKey: "NSCampaigns.colFailed" },
];

const CampaignDetail = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { id } = useParams();
    const detail = useSelector((s) => s.campaignReducer.detail);
    const analytics = useSelector((s) => s.campaignReducer.analytics);
    const [window, setWindow] = useState(null);

    useEffect(() => {
        if (id) dispatch(fetchCampaign(id));
    }, [id, dispatch]);

    useEffect(() => {
        if (id) dispatch(fetchCampaignAnalytics(id, window));
    }, [id, window, dispatch]);

    if (!detail) return <p className={styles.loading}>...</p>;

    const delivery = analytics?.delivery || {};
    const conversion = analytics?.conversion || {};
    const eligible = conversion.eligible || 0;
    const returned = conversion.returned || 0;
    const rate = Math.round((conversion.rate || 0) * 100);
    const activeWindow = conversion.window || window || 14;

    return (
        <div className={styles.container}>
            <PageHeader title={detail.audience_label || detail.name} />

            <div className={styles.detailMeta}>
                <span className={`${styles.statusBadge} ${styles[`status_${detail.status}`]}`}>
                    {t(STATUS_LABEL_KEY[detail.status] || detail.status)}
                </span>
                <span className={styles.progress}>
                    {t("NSCampaigns.progressLabel", {
                        sent: detail.recipients_sent || 0,
                        total: detail.recipients_total || 0,
                    })}
                </span>
            </div>

            <div className={styles.actions}>
                {detail.status === "in_progress" && (
                    <ButtonFilled onClick={() => dispatch(pauseCampaign(id))}>
                        {t("NSCampaigns.actionPause")}
                    </ButtonFilled>
                )}
                {detail.status === "paused" && (
                    <ButtonFilled onClick={() => dispatch(resumeCampaign(id))}>
                        {t("NSCampaigns.actionResume")}
                    </ButtonFilled>
                )}
                {(detail.status === "in_progress" || detail.status === "paused") && (
                    <ButtonFilled onClick={() => dispatch(cancelCampaign(id))}>
                        {t("NSCampaigns.actionCancel")}
                    </ButtonFilled>
                )}
            </div>

            <h3 className={styles.sectionTitle}>{t("NSCampaigns.deliverySection")}</h3>
            <div className={styles.statCards}>
                {DELIVERY_CARDS.map((card) => (
                    <div key={card.key} className={styles.statCard}>
                        <span className={styles.statValue}>{delivery[card.key] || 0}</span>
                        <span className={styles.statLabel}>{t(card.labelKey)}</span>
                    </div>
                ))}
            </div>

            <h3 className={styles.sectionTitle}>{t("NSCampaigns.conversionSection")}</h3>
            <div className={styles.conversionBlock}>
                <p className={styles.conversionText}>
                    {t("NSCampaigns.conversionResult", { returned, eligible, rate })}
                </p>
                <div className={styles.filterBar}>
                    {WINDOW_OPTIONS.map((w) => (
                        <button
                            key={w}
                            type="button"
                            className={activeWindow === w ? styles.filterActive : styles.filter}
                            onClick={() => setWindow(w)}
                        >
                            {t("NSCampaigns.windowDays", { days: w })}
                        </button>
                    ))}
                </div>
            </div>

            <RecipientList campaignID={id} />
        </div>
    );
};

export default CampaignDetail;
